import { BrowserRouter, Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Home } from "./pages/Home";
import { Bills } from "./pages/Bills";
import { BillPage } from "./pages/BillPage";
import { Laws } from "./pages/Laws";
import { LawPage } from "./pages/LawPage";
import { Members } from "./pages/Members";
import { MemberPage } from "./pages/MemberPage";
import { Divided } from "./pages/Divided";
import { Lobbying } from "./pages/Lobbying";
import { FirmPage } from "./pages/FirmPage";
import { LobbyistPage } from "./pages/LobbyistPage";
import { PartyCommittees } from "./pages/PartyCommittees";
import { CommitteePage } from "./pages/CommitteePage";
import { About } from "./pages/About";

/** A single-page app keeps the scroll position across a navigation, which
 *  lands you halfway down a law you have not read yet. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Braces, not an arrow shorthand. Shorthand returns whatever scrollTo
    // returns, React takes an effect's return value to be its cleanup, and
    // the next navigation calls it -- so the crash is not on the page that
    // set it but on the one after, which is a horrible thing to chase.
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Nav() {
  const link = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-1.5 text-sm font-medium transition ${
      isActive ? "bg-sunk text-ink" : "text-muted hover:text-ink"
    }`;

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-paper/85 backdrop-blur">
      {/* Wraps rather than overflowing: the bar is six links wide now and
          the longest two are on the money side. */}
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-5 py-3">
        <Link to="/" className="mr-auto flex items-baseline gap-2">
          <span className="font-display text-lg font-semibold tracking-tight">
            do you know the law
          </span>
        </Link>
        <NavLink to="/bills" className={link}>
          In progress
        </NavLink>
        <NavLink to="/laws" className={link}>
          Browse laws
        </NavLink>
        <NavLink to="/members" className={link}>
          Who voted how
        </NavLink>
        <NavLink to="/money" end className={link}>
          Follow the money
        </NavLink>
        <NavLink to="/money/committees" className={link}>
          Party committees
        </NavLink>
        <NavLink to="/about" className={link}>
          About
        </NavLink>
        <a
          href="/docs"
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-muted transition hover:text-ink"
        >
          API
        </a>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-24 border-t border-line">
      <div className="mx-auto max-w-6xl px-5 py-10 text-sm text-muted">
        <p className="max-w-2xl">
          Built from the government's own published record: bill status and text from{" "}
          <a className="text-brand hover:underline" href="https://www.govinfo.gov/bulkdata">
            govinfo bulk data
          </a>
          , roll calls from the Clerk of the House and the Secretary of the Senate.
        </p>
        <p className="mt-3 max-w-2xl">
          Plain-English summaries are generated by a language model from each law's own text.
          They carry the grounding they were built from. Read the law before relying on one.
        </p>
        <p className="mt-4">
          <a className="text-brand hover:underline" href="/docs">
            The API is free and needs no key.
          </a>
        </p>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-lg focus:bg-ink focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <Nav />
      <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/bills" element={<Bills />} />
          {/* Singular, to match `/law/:billId`. Both are keyed on the same
              bill id; which of the two a bill has a page at is only whether
              it got to the end. */}
          <Route path="/bill/:billId" element={<BillPage />} />
          <Route path="/laws" element={<Laws />} />
          <Route path="/law/:billId" element={<LawPage />} />
          <Route path="/members" element={<Members />} />
          <Route path="/member/:bioguide" element={<MemberPage />} />
          <Route path="/divided/:band" element={<Divided />} />
          <Route path="/money" element={<Lobbying />} />
          {/* Ahead of `:registrantId` on purpose. Router ranking would put
              the literal segment first anyway, but the two are one path
              apart and the firm route takes a number, so a reordering would
              send this to a firm page for NaN. */}
          <Route path="/money/committees" element={<PartyCommittees />} />
          <Route path="/money/committees/:committeeId" element={<CommitteePage />} />
          <Route path="/money/:registrantId" element={<FirmPage />} />
          {/* Nested under the organisation because that is half the key: no
              federal form identifies a lobbyist across a change of employer,
              so there is no /lobbyist/:name to give them. */}
          <Route path="/money/:registrantId/lobbyist/:key" element={<LobbyistPage />} />
          <Route path="/about" element={<About />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">That page is not here.</h1>
      <p className="mt-3 text-muted">
        It may have been a law id we do not hold. The corpus covers the 117th Congress onwards.
      </p>
      <Link
        to="/laws"
        className="mt-6 inline-block rounded-lg bg-ink px-5 py-2.5 text-sm font-medium text-white"
      >
        Browse every law
      </Link>
    </div>
  );
}
